// @flow
import type { State } from "@store/reducers";
import type { ApprovableContainer, HierarchyAssignment } from "@modules/service/types";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";
import { validateHierarchyAssignmentSchema } from "./index";

// ABSENCE MONITORING CRUD

export const getAbsenceMonitoringList = (
  state: State,
): Array<ApprovableContainer<AbsenceMonitoring>> => state.service.absenceMonitoring.list;

export const getAbsenceMonitoringActiveList = (state: State): Array<AbsenceMonitoring> => {
  return getAbsenceMonitoringList(state)
    .filter(c => c.approvalState.indexOf("ACTIVE_") !== -1)
    .reduce((acc, c) => (c.active ? [...acc, { ...c.active, recordId: c.id }] : acc), []);
};

export const getAbsenceMonitoringPendingList = (state: State): Array<AbsenceMonitoring> => {
  return getAbsenceMonitoringList(state)
    .filter(c => c.approvalState.indexOf("PENDING_REVIEW") !== -1)
    .reduce((acc, c) => (c.draft ? [...acc, { ...c.draft, recordId: c.id }] : acc), []);
};

export const getAbsenceMonitoringDraftList = (state: State): Array<AbsenceMonitoring> => {
  return getAbsenceMonitoringList(state)
    .filter(c => c.approvalState === "DRAFT_ONLY" || c.approvalState === "ACTIVE_WITH_DRAFT")
    .reduce((acc, c) => (c.draft ? [...acc, { ...c.draft, recordId: c.id }] : acc), []);
};

export const getCurrentAbsenceMonitoring = (
  state: State,
): ?ApprovableContainer<AbsenceMonitoring> => state.service.absenceMonitoring.current;

export const getAbsenceMonitoringDraft = (state: State): ?AbsenceMonitoring =>
  state.service.absenceMonitoring.draft;

export const getAbsenceMonitoringAssignments = (state: State): Array<HierarchyAssignment> =>
  state.service.absenceMonitoring.assignments;

export const getAbsenceMonitoringDraftAssignments = (state: State): Array<HierarchyAssignment> =>
  state.service.absenceMonitoring.draftAssignments;

export const isAbsenceMonitoringDraftAssignmentsValid = (state: State): boolean => {
  const assignments = getAbsenceMonitoringDraftAssignments(state);
  return validateHierarchyAssignmentSchema(assignments);
};

export const isAbsenceMonitoringAssignmentsValid = (state: State): boolean => {
  const assignments = getAbsenceMonitoringAssignments(state);
  return validateHierarchyAssignmentSchema(assignments);
};

export const getAbsenceMonitoringAppealConfig = (
  state: State,
): {
  appealReasons: Array<Object>,
  outcomeReasons: Array<Object>,
} => {
  const current = getCurrentAbsenceMonitoring(state);
  return {
    appealReasons: current?.appealConfig?.appealReasons || [],
    outcomeReasons: current?.appealConfig?.outcomeReasons || [],
  };
};

// ABSENCE MONITORING SCHEMA

export const getAbsenceMonitoringSchema = (state: State): Object => {
  return state.service.absenceMonitoring.schema;
};
